import { env } from '@repo/env';
import pino, { type Logger, type LoggerOptions } from 'pino';
import { defaultBindings } from './constants';

export type LoggerType = Logger<'critical'>;

const LOG_FUNCTIONS = {
  critical: console.error,
  debug: console.debug,
  error: console.error,
  fatal: console.error,
  info: console.info,
  // no-op for silent logging
  silent: () => { },
  trace: console.debug,
  warn: console.warn,
} as const;

type Bindings = {
  component: string;
  path: string;
} & pino.Bindings;

/**
 * Creates and returns a Pino logger instance with specified bindings.
 * @param {Object} bindings - An object containing bindings for the logger.
 * @param {string} bindings.component - The component name to bind to the logger.
 * @param {string} bindings.path - The path to bind to the logger.
 * @param {Object<string, string>} bindings - Additional key-value pairs to bind to the logger.
 * @returns {Logger} A Pino logger instance configured with the specified bindings.
 */
export function loggerFactory(bindings: Bindings) {
  const bindingsWithDefault = { ...defaultBindings, ...bindings };
  const IS_DEV_ENV = env.NEXT_PUBLIC_DEPLOYABLE_ENV === 'dev';
  const IS_EDGE = process.env.NEXT_RUNTIME === 'edge';
  const level = IS_DEV_ENV ? 'trace' : (env.LOG_LEVEL ?? 'info');
  const options: LoggerOptions<'critical'> = {
    level,
    customLevels: {
      // `critical` is just an alias for `fatal`. This is required for DataDog to correctly parse and show "critical" logs.
      critical: 60,
    },
    formatters: {
      level(label) {
        return { level: label };
      },
      bindings() {
        return bindingsWithDefault;
      },
    },
    timestamp: pino.stdTimeFunctions.isoTime,
  };

  // This is required for correctly parsing edge logs.
  // Edge environments like NextJS middleware get detected as a browser rather than server side.
  // See https://github.com/pinojs/pino/issues/1613
  if (IS_EDGE) {
    return pino({
      ...options,
      serialize: true,
      browser: {
        // @ts-expect-error Pino types `obj` as `object`
        write: (obj: { level: string | number; [key: string]: string | number }) => {
          try {
            const numericLevel = Number(obj.level);
            const label = numericLevel === options?.customLevels?.critical ? 'critical' : pino.levels.labels[numericLevel];
            const payload = JSON.stringify({
              ...obj,
              ...bindingsWithDefault,
              level: label,
            });

            const log = LOG_FUNCTIONS[label as keyof typeof LOG_FUNCTIONS] ?? console.log;
            log(payload);
          } catch (err) {
            if (err instanceof Error) {
              console.error(
                JSON.stringify({
                  ...bindingsWithDefault,
                  name: err.name,
                  message: err.message,
                  stack: err.stack,
                }),
              );
            } else {
              console.error(
                JSON.stringify({ ...bindingsWithDefault, message: 'Unknown error type' }),
              );
            }
          }
        },
      },
    });
  }

  return pino(options);
}
